// @flow
import React, { useEffect, useCallback, type Element } from "react";
import { useSelector, useDispatch } from "react-redux";
import { usePrevious } from "hooks";
import { compose } from "redux";
import FolderTree from "components/FolderTree";
import { isValidItemUri, isSpecialFolderItemUri } from "data/utils";
import { getTreeAction, togglesUpdateCounterAction } from "data/actions";
import * as s from "data/reducers/selectors";
import {
    ServerEventActionType,
    ServerEventItemType,
    type TreeItemDTO,
} from "data/types";
import Constants from "data/constants";
import Status from "components/Status";
import GlobalEventsHandler from "containers/GlobalEventsHandler";
import NoData from "components/NoData";
import loc from "i18next";
import { MultiSelectionContext } from "data/context";

type Props = {
    /** current itemUri */
    itemUri: string,
    /** current selected tree node's id */
    activeTreeNodeId: number,
    /** text filtering */
    filter: ?string,
    /** whether to include subfolders when text filtering */
    includeSubfolders: boolean,
    /** callback when a tree item is clicked
     * @param {string} itemUri tree node's itemUri     * @param {number} nodeId tree node's id
     */
    onClick: (itemUri: string, nodeId: number) => void,
    /** callback when a tree item is expanded
     * @param {string} itemUri tree node's itemUri
     */
    onExpand: (itemUri: string) => void,
    /** callback when tree is being scrolled */
    onScroll?: () => void,
    /** GlobalEventsHandler.refreshId */
    refreshId: number,
    /** optional NoData Content Component */
    NoDataContent: Element<any>,
    /** whether list is currently focused
     * @default {boolean} true
     */
    focused?: boolean,
};

export const FolderTreeContainer = ({
    itemUri,
    viewName = "folder",
    activeTreeNodeId,
    filter,
    includeSubfolders,
    onClick,
    onExpand,
    onScroll,
    refreshId,
    NoDataContent,
    focused = true,
}: Props) => {
    const dispatch = useDispatch();
    // redux state
    const status = useSelector(s.treeStatusSelector);
    const error = useSelector(s.treeErrorSelector);
    const treeNodes = useSelector(s.treeNodesSelector);
    const totalCount = useSelector(s.treeTotalCountSelector);
    const isBrowsable = useSelector(s.treeIsBrowsableSelector);
    const nextPage = useSelector(s.treeNextPageSelector);
    const prevPage = useSelector(s.treePrevPageSelector);
    const prevItemUri = usePrevious(itemUri);
    const prevFilter = usePrevious(filter);
    const prevRefreshId = usePrevious(refreshId);
    const prevIncludeSubfolders = usePrevious(includeSubfolders);

    const handleUpdateToggleCounter = useCallback(
        (count: number, loading?: boolean, status?: string): void =>
            dispatch(
                togglesUpdateCounterAction({
                    name: viewName,
                    counts: { tree: count },
                    status: {
                        tree: loading === true ? Constants.LOADING : status,
                    },
                })
            ),
        [dispatch, viewName]
    );

    useEffect(() => {
        /* istanbul ignore next */
        if (
            (prevItemUri === itemUri &&
                prevRefreshId === refreshId &&
                prevFilter === filter &&
                prevIncludeSubfolders === includeSubfolders) ||
            !isValidItemUri(itemUri)
        ) {
            return;
        }
        // fetch results
        dispatch(
            getTreeAction({
                itemUri,
                filter,
                isDeep: includeSubfolders,
                pageNum: 0,
                selectedItem: activeTreeNodeId,
                refresh: true,
            })
        );
    }, [
        dispatch,
        itemUri,
        prevItemUri,
        filter,
        prevFilter,
        includeSubfolders,
        prevIncludeSubfolders,
        refreshId,
        prevRefreshId,
        handleUpdateToggleCounter,
        activeTreeNodeId,
    ]);

    // update tree toggle:
    // show totalCount of results,
    // show if results are filtered,
    // show if there are more results available
    useEffect(() => {
        if (status === Constants.OK)
            handleUpdateToggleCounter(
                totalCount,
                false,
                filter && String(filter).length > 0
                    ? "filter"
                    : treeNodes && treeNodes.length < totalCount
                    ? "forward-step"
                    : null
            );
        else if (status === Constants.LOADING)
            // start loading icon
            handleUpdateToggleCounter(0, true);
        else if (status === Constants.ERROR) {
            handleUpdateToggleCounter(0, false, status);
        }
    }, [handleUpdateToggleCounter, totalCount, status, treeNodes, filter]);

    // user clicks a tree node
    const handleClick = useCallback(
        (treeNode: TreeItemDTO, e?: SyntheticMouseEvent<*>) => {
            /* istanbul ignore next */
            if (e) {
                e.preventDefault();
                e.stopPropagation();
            }
            onClick(treeNode.itemUri, treeNode.id);
        },
        [onClick]
    );

    // user expands a tree node
    const handleExpand = useCallback(
        (treeNode: TreeItemDTO, e?: SyntheticMouseEvent<*>) => {
            /* istanbul ignore if */
            if (
                !isValidItemUri(treeNode.itemUri) ||
                isSpecialFolderItemUri(treeNode.itemUri)
            ) {
                return;
            }
            /* istanbul ignore next */
            if (e) {
                e.preventDefault();
                e.stopPropagation();
            }
            onExpand(treeNode.itemUri);
        },
        [onExpand]
    );

    const handleLoadMore = useCallback(
        (pageNum = nextPage, selectedItem = null) => {
            // start loading icon
            handleUpdateToggleCounter(0, true);
            // fetch results
            dispatch(
                getTreeAction({
                    itemUri,
                    filter,
                    isDeep: includeSubfolders,
                    pageNum,
                    selectedItem,
                })
            );
        },
        [
            dispatch,
            handleUpdateToggleCounter,
            itemUri,
            filter,
            includeSubfolders,
            nextPage,
        ]
    );

    // Reset activeTreeNodeId if it does not exist anymore
    useEffect(() => {
        if (
            filter ||
            !totalCount ||
            !treeNodes?.length ||
            activeTreeNodeId == null ||
            treeNodes.some(
                (node) => node.id === decodeURIComponent(activeTreeNodeId)
            )
        ) {
            return;
        }
        onClick(treeNodes[0].itemUri, treeNodes[0].id);
    }, [
        handleLoadMore,
        totalCount,
        treeNodes,
        activeTreeNodeId,
        onClick,
        filter,
    ]);

    if (status !== Constants.OK && !(nextPage || prevPage)) {
        return <Status status={status} error={error} />;
    }

    // do not render further if this folder is non-browsable!
    if (isBrowsable === false) {
        return (
            <NoData locContext="folder">
                <div>{loc.t("folder:nonBrowsable")}</div>
            </NoData>
        );
    } else {
        return (
            // #69379 fix
            <MultiSelectionContext.Provider value={{}}>
                <FolderTree
                    itemUri={itemUri}
                    activeTreeNodeId={activeTreeNodeId}
                    focused={focused}
                    tree={treeNodes}
                    filter={filter}
                    onClick={handleClick}
                    onDoubleClick={handleExpand}
                    onLoadMore={handleLoadMore}
                    onExpand={handleExpand}
                    onScroll={onScroll}
                    NoDataContent={NoDataContent}
                    totalCount={totalCount}
                    viewName={viewName}
                    status={status}
                    nextPage={nextPage}
                    prevPage={prevPage}
                />
            </MultiSelectionContext.Provider>
        );
    }
};

export default compose(
    GlobalEventsHandler({
        actions: [
            ServerEventActionType.add,
            ServerEventActionType.modify,
            ServerEventActionType.refresh,
            ServerEventActionType.delete,
        ],
        items: [ServerEventItemType.folder],
    })
)(FolderTreeContainer);
